import { ErrorHandler, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Auth } from 'aws-amplify';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpService } from 'src/app/core/services/http.service';
import { reportError } from 'src/app/core/services/sentry.utils';
import * as fromRoot from '../../reducers';
import * as AuthActions from '../actions/auth.actions';
import {
  ForgottenPasswordForm,
  SubmitNewPasswordForm
} from '../models/LoginSubmission';
import { ClinicInfoResponse } from '../models/server-response/clinic-info-response';
import { environment } from './../../../environments/environment';
import { PrimaryLogin } from './../models/PrimaryLogin';
import { CompletePasswordForm, VerifyOTPParams } from './../models/user';

@Injectable()
export class AuthService {
  constructor(
    private _store: Store<fromRoot.State>,
    private _errorHandler: ErrorHandler,
    private _httpService: HttpService
  ) {
    this._store.dispatch(new AuthActions.GetClinicId());
  }

  public getClinicId(
    hostname = window.location.hostname
  ): Observable<ClinicInfoResponse> {
    if (
      environment.environment === 'local' ||
      (environment.environment === 'test' &&
        (hostname === 'localhost' || hostname === '127.0.0.2'))
    ) {
      const defaultSubdomain = 'meditex1';
      //// you can map 127.0.0.x IP addresses to different subdomains for local testing of multiple clinics
      // const subdomain =
      //   hostname === '127.0.0.2' ? 'vrepro-salve-validation' : defaultSubdomain;
      hostname = `${defaultSubdomain}.${hostname}`;
      // hostname = `testsalveclinicv2.${hostname}`;
      // hostname = `testsalveclinic.${hostname}`;
      // hostname = `ccrmtest.${hostname}`;
      // hostname = `testsalvecore.${hostname}`;
      // hostname = 'uk-demo.salvehealth.co';
      // hostname = `mathe-mellowood-consent.${hostname}`;
      // hostname = `vrepro-salve-validation.${hostname}`;
    }

    const postParams = {
      Action: 'getclinicbysubdomain',
      Input: hostname
    };

    return this._httpService
      .performPublicPostRequest(postParams, '/clinicbysubdomain')
      .pipe(
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  public async cognitoLogout() {
    return Auth.signOut();
  }

  public async cognitoLogin(primaryLoginInfo: PrimaryLogin): Promise<any> {
    let cognitoUser: any;

    try {
      cognitoUser = await Auth.signIn(primaryLoginInfo);
    } catch (e) {
      reportError(e, 'auth.cognitoLogin');
      return {
        message: `Error Signing in cognito user`,
        originalError: e
      };
    }

    return cognitoUser;
  }

  public async verifyOTP(params: VerifyOTPParams): Promise<any> {
    let cognitoUser: any;

    try {
      cognitoUser = await Auth.sendCustomChallengeAnswer(
        params.user,
        params.code
      );
    } catch (e) {
      reportError(e, 'auth.verifyOTP');
      return {
        message: `Error Verify OTP`,
        originalError: e
      };
    }

    return cognitoUser;
  }

  public async cognitoCompletePassword(
    submissionData: CompletePasswordForm
  ): Promise<any> {
    const { user, password, requiredAttributes } = submissionData;

    try {
      /**
       * Implementing direct call to CognitoUser object by the example of how Amplify's Auth.completeNewPassword does it. Their completeNewPassword method is essentially a wrapper with some preset logic.
       * But in contrast to Amplify we implement the customChallenge callback that we must provide for our use case. And this makes the whole flow work.
       */
      return await new Promise((resolve, reject) => {
        user.completeNewPasswordChallenge(password, requiredAttributes, {
          onSuccess: () => {
            resolve({ user });
          },
          onFailure: (err) => {
            console.error('Failed to complete password challenge', err);
            reject(err);
          },
          customChallenge: () => {
            // this is the main callback that will get called probably 100% of the time because we in our Cognito lambdas always return customChallenge (which means OTP) after user sets their password for the first time
            // down the line OTPRequired will be dispatched which will navigate to OTP page
            resolve({ result: 'customChallenge', user });
          }
        });
      });
    } catch (e) {
      reportError(e, 'auth.completePassword');
      return {
        message: `Error Completing New Password`,
        originalError: e
      };
    }
  }

  public async cognitoResetPassword(
    submissionData: SubmitNewPasswordForm
  ): Promise<any> {
    const { email, code, new_password } = submissionData;
    let res;

    try {
      res = await Auth.forgotPasswordSubmit(email, code, new_password);
    } catch (e) {
      reportError(e, 'auth.resetPassword');
      console.error(e);
      throw new Error(`Error Completing New Password`);
    }

    return res;
  }

  public async cognitoGenerateResetCode(
    submissionData: ForgottenPasswordForm
  ): Promise<any> {
    const { email } = submissionData;
    let res;

    try {
      res = await Auth.forgotPassword(email);
    } catch (e) {
      reportError(e, 'auth.generateResetCode');
      console.error(e);
      throw new Error(`Error Generating Code`);
    }

    return res;
  }
}
